import React, { useState, useEffect } from 'react';
import logo from 'media/pngs/logo.png';
import SVG from 'components/renderSvg';
import InputField from 'components/inputField';
import Button from 'components/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import passwordIcon from '../../../media/svgs/password.svg';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { verifyRegistrationOTP, resendRegistrationOTP } from '../../../services/vendorAPI';

const otpSchema = yup.object().shape({
    otp: yup.string().length(6, 'OTP must be 6 digits').required('OTP is required'),
});

const VerifyOtpPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loader, setLoader] = useState(false);
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);

    const email = location.state?.email;

    useEffect(() => {
        if (!email) {
            toast.error('Email not found. Please register again.');
            navigate('/register');
        }
    }, [email, navigate]);

    useEffect(() => {
        let interval = null;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else {
            setCanResend(true);
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [timer]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(otpSchema),
        defaultValues: { otp: '' },
    });

    const onVerify = async (data) => {
        setLoader(true);
        try {
            const response = await verifyRegistrationOTP(email, data.otp);
            if (response.success) {
                toast.success('Account verified successfully! You can now login.');
                navigate('/login');
            } else {
                toast.error(response.error || 'Verification failed');
            }
        } catch (error) {
            toast.error(error.message || 'Verification failed');
        } finally {
            setLoader(false);
        }
    };

    const onResend = async () => {
        if (!canResend) return;
        setLoader(true);
        try {
            const response = await resendRegistrationOTP(email);
            if (response.success) {
                toast.success('OTP sent again');
                setTimer(60);
                setCanResend(false);
            } else {
                toast.error(response.error || 'Failed to resend OTP');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to resend OTP');
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className='flex justify-center items-center h-screen flex-col bg-[#f7f7f8]'>
            <div className='flex flex-col items-center gap-1 mb-6'>
                <img src={logo} alt='logo' className="mb-4" />
                <h1 className='text-primary font-medium text-[24px]'>Verify Your Account</h1>
                <p className='text-[#3f3f50] font-normal text-[16px] text-center max-w-[400px]'>
                    We've sent a 6-digit verification code to <span className="font-bold">{email}</span>. Please enter it below.
                </p>
            </div>

            <div className='bg-white shadow-sm p-8 rounded-[16px] md:w-[500px] xs:w-full sm:w-full'>
                <form onSubmit={handleSubmit(onVerify)}>
                    <InputField
                        type='text'
                        name='otp'
                        label='Verification Code'
                        placeholder='Enter 6-digit OTP'
                        maxLength={6}
                        error={errors?.otp?.message}
                        register={register}
                        icon={<SVG icon={passwordIcon} />}
                        autoFocus
                    />
                    <Button
                        title='Verify OTP'
                        variant='success'
                        className='w-full mt-4'
                        disabled={loader}
                    />

                    <div className="mt-6 text-center">
                        <p className="text-gray-500 text-sm mb-2">Didn't receive the code?</p>
                        <button
                            type="button"
                            className={`font-medium text-lg underline ${canResend ? 'text-[#7047EB] cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}
                            onClick={onResend}
                            disabled={!canResend || loader}
                        >
                            {canResend ? 'Resend OTP' : `Resend OTP in ${timer}s`}
                        </button>
                    </div>

                    <div className='mt-4 text-center'>
                        <span
                            className='text-primary cursor-pointer underline font-medium'
                            onClick={() => navigate('/register')}
                        >
                            Back to Register
                        </span>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VerifyOtpPage;
